<template>
  <div v-if="canView()" class="h-100">
    <div v-if="loadingCompany || loadingUser" class="h-100 d-flex justify-content-center align-items-center">
      <Loading />
    </div>
    <div v-else-if="company && !company.disabled && company.approved && !brandUnavailable" class="h-100">
      <BNavbar v-if="tab !== 'Menu' || !loggedIn" class="header d-print-none d-block border-bottom m-0 px-3 py-3" variant="white">
        <div class="row m-0 align-items-center">
          <BushelBtn
            v-if="cartVisible && !isOwnStorefront"
            class="btn-sm"
            btn-container-class=""
            outline
            variant="primary"
            :ripple="false"
            @click="toggleCheckout"
          >
            <i class="fas fa-arrow-left mr-1" />
            Return to Menu
          </BushelBtn>
          <template v-else>
            <a :href="appUrl" class="d-flex align-items-center mr-3 text-decoration-none">
              <img
                :src="industry == 'cannabis' ? '/img/logos/apex-logo.png' : '/img/logos/bushel-logo.png'"
                :alt="industry == 'cannabis' ? 'Apex Trading' : 'Bushel44'"
                style="height: 28px"
              />
              <i class="fas fa-external-link-alt text-secondary ml-2" style="font-size: 0.75em" />
            </a>
            <BushelBtn
              v-if="tab !== 'Menu'"
              :class="`btn-sm d-none d-sm-inline-block`"
              btn-container-class=""
              variant="primary"
              :ripple="false"
              :outline="tab !== 'Menu'"
              @click="tab = 'Menu'"
            >
              <i class="fas fa-cannabis" />
              View Menu
            </BushelBtn>
          </template>

          <div class="ml-auto d-flex align-items-center">
            <BushelBtn
              v-if="!showProfileChat"
              :class="`btn-sm d-none d-sm-inline-block mr-2`"
              btn-container-class=""
              variant="primary"
              :outline="tab !== 'Contact'"
              :ripple="false"
              @click="tab = 'Contact'"
            >
              <i class="far fa-envelope" />
              Contact Sales
            </BushelBtn>
            <CompanyChatWindowWithTrigger
              v-if="showProfileChat"
              class="mr-2 storefront-chat-trigger"
              btn-class="btn-sm"
              :authorized="true"
              :user="user"
              :company="company"
            />
            <BushelSelect
              v-if="userCanShop && !isOwnStorefront && massagedOptions.length > 1 && !cartVisible"
              :value="activeCart"
              label="name"
              no-deselect
              no-padding
              custom-option-names
              class="d-inline-block storefront-cart-select"
              :options="massagedOptions"
              hide-main-label
              track-by="name"
              @select="(option) => changeActiveCart(option)"
            />

            <BushelBtn
              v-if="userCanShop && !isOwnStorefront && !cartVisible"
              variant=""
              btn-container-class="p-0"
              :ripple="false"
              class="ml-2"
              flat
              @click="toggleCheckout()"
            >
              <img src="/img/icons/store-cart.svg" alt="Store Cart" height="35px" width="35px" />
              <BBadge v-if="activeCart && activeCart.cart_items_count" class="badge position-absolute" variant="success" style="right: 6px; top: 3px">
                {{ activeCart.cart_items_count }}
              </BBadge>
            </BushelBtn>
            <BushelBtn
              v-if="tab == 'Menu' && !loggedIn"
              :class="`btn-sm ml-2 d-none d-sm-inline-block`"
              btn-container-class=""
              variant="success"
              :ripple="false"
              :outline="false"
              @click="view == 'profile' ? (showAuthModal = true) : redirectToStorefront()"
            >
              <i class="fas fa-lock" />
              Log In to Order
            </BushelBtn>
          </div>

          <b-nav-item-dropdown text="Pages" right class="d-sm-none text-primary nav">
            <b-dropdown-item @click="cdpModalState = true">
              <i class="fas fa-home text-primary" />
              Company Overview
            </b-dropdown-item>
            <b-dropdown-item @click="tab = 'Menu'">
              <i class="fas fa-cannabis text-primary" />
              View Menu
            </b-dropdown-item>
            <b-dropdown-item @click="tab = 'Contact'">
              <i class="far fa-envelope text-primary" />
              Contact Sales
            </b-dropdown-item>
          </b-nav-item-dropdown>
          <BNavItemDropdown
            v-if="loggedIn && user"
            style="list-style: none"
            :class="`settingsDropdown text-secondary ${userCanShop || showProfileChat ? '' : 'ml-auto'}`"
            toggle-class="text-decoration-none"
            no-caret
            right
          >
            <template #button-content>
              <i style="font-size: 1.33333em" class="fa text-secondary fa-cog px-2" />
            </template>
            <b-dropdown-item :href="appUrl">
              <i class="fas fa-home mr-1" />
              Go to Home
            </b-dropdown-item>
            <BDropdownDivider />
            <b-dropdown-item v-if="loggedIn && hasSwitchableCompanies()" @click="showCompanySelectorModal = true">
              <i class="far fa-building mr-1" />
              Companies
            </b-dropdown-item>
            <BDropdownDivider />
            <b-dropdown-item @click="cdpModalState = true">
              <i class="far fa-paper-plane mr-1" />
              Contact Seller
            </b-dropdown-item>
            <BDropdownDivider />
            <b-dropdown-item @click="onLogout">
              <i class="fas fa-sign-out-alt mr-1" />
              Logout
            </b-dropdown-item>
          </BNavItemDropdown>
        </div>
      </BNavbar>

      <div class="d-flex flex-column" :style="tab === 'Menu' && loggedIn ? 'height: 100%' : 'height: calc(100% - 64.6px)'">
        <div v-if="loggedIn && user && !user.email_verified_at" class="alert alert-warning text-center mb-0 rounded-0">
          <i class="fas fa-envelope mr-1" />
          To ensure security, please verify your email to start shopping.
          <a href="#" class="font-weight-bold" @click.prevent="resendVerification">{{
            resendingVerification ? "Sending..." : "Resend verification email"
          }}</a>
        </div>
        <div v-if="userCanShop && !activeBuyerCanOrder" class="alert alert-warning text-center mb-0 rounded-0">
          <i class="fas fa-ban mr-1" />
          <template v-if="activeBuyer && activeBuyer.hold && activeBuyer.hold_message">{{ activeBuyer.hold_message }}</template>
          <template v-else>This location has been blocked by {{ company.name }} from placing orders.</template>
        </div>
        <VendorDisabledAlert v-if="userCanShop && vendorDisabled" :company="company" @enabled="vendorDisabled = false" />
        <div v-if="loading && !loadingError" class="h-100 row m-0 align-items-center">
          <Loading />
        </div>
        <div v-if="!loading && loadingError" class="h-100 row m-0 align-items-center justify-content-center">
          <div class="alert alert-info small"><b>Connection Issue.</b> Please contact support.</div>
        </div>
        <CompanyDisplay
          v-if="!loading && company && company.id && cdpModalState"
          :company="company"
          :show="cdpModalState"
          @hide="cdpModalState = false"
        />
        <div v-if="tab == 'Menu'" class="flex-fill" style="min-height: 0">
          <div v-if="theme && company && company.id" class="h-100 sf-root" :style="theme.css_variables">
            <!-- The listing and the product page are routes so filters, sorting and products
                 are all addressable; this view stays the chrome around whichever is showing.
                 Bound directly rather than through a slot so the routes' own props still apply. -->
            <router-view
              class="flex-fill"
              v-bind="listingProps"
              @add-to-cart="addToCartAction"
              @toggle-checkout="toggleCheckout()"
              @contact-sales="tab = 'Contact'"
              @login-requested="showAuthModal = true"
            />
          </div>
        </div>

        <Contact v-if="tab == 'Contact' && !loading" :company="company" :user="user" class="mt-4" />
      </div>
    </div>
    <div v-else class="border text-center mt-5 mx-auto p-5" style="max-width: 1000px">
      <h3>Hi thanks for your interest. The menu you are trying to access is no longer available.</h3>
      <div v-if="!loggedIn">
        <h5>Don't worry, we have an entire marketplace of wholesale you can shop from. Click below to login or create to your free buyer account.</h5>
        <div class="mt-3">
          <BushelBtn
            :class="`btn-sm ml-auto d-none d-sm-inline-block`"
            btn-container-class=""
            variant="primary"
            :ripple="false"
            outline
            @click="registerRoute()"
          >
            Sign Up
          </BushelBtn>

          <BushelBtn
            :class="`btn-sm ml-2 d-none d-sm-inline-block`"
            btn-container-class=""
            variant="primary"
            :ripple="false"
            outline
            @click="loginRoute()"
          >
            Login
          </BushelBtn>
        </div>
      </div>
      <div v-else>
        <h5>Don't worry, we have an entire marketplace of wholesale you can shop from. Click below to go to the marketplace.</h5>
        <div class="mt-3">
          <BushelBtn
            :class="`btn-sm ml-auto d-none d-sm-inline-block`"
            btn-container-class=""
            variant="primary"
            :ripple="false"
            outline
            :href="appUrl"
          >
            Go to Marketplace
          </BushelBtn>
        </div>
      </div>
    </div>
    <BulkDiscountModal
      v-if="bulkDiscounts"
      :show="isShowingBulkDiscountModal"
      :bulk-discounts="bulkDiscounts"
      @hide="isShowingBulkDiscountModal = false"
    />
    <StoreAuthModal :visible="showAuthModal" @logged-in="onLoggedIn" @hide="showAuthModal = false" />
    <CompanySelector
      v-if="showCompanySelectorModal && loggedIn"
      :visible="showCompanySelectorModal"
      :companies="companies"
      :seller-company="company"
      :user="user"
      :selected-company="user.current_company"
      @hide="showCompanySelectorModal = false"
      @selected="onSelectCompany"
    />
    <CreateOperationModal
      v-if="showCreateOperationsModal"
      :visible="showCreateOperationsModal"
      :current-company="user.current_company"
      :seller-company-id="company.id"
      :seller-company-name="company.name"
      @operation-created="onOperationCreated"
    />
    <WaitApprovalModal v-if="showWaitApprovalModal" :visible="showWaitApprovalModal" @hide="closeWaitApprovalModal" />
    <WhileWaitingModal v-if="showWhileWaitingModal" :visible="showWhileWaitingModal" :reason="whileWaitingReason" @close="closeWhileWaitingModal" />
    <SendLeadModal
      v-if="showSendLeadModal && company"
      :visible="showSendLeadModal"
      :seller-company="company"
      @request-access="onRequestAccess"
      @close="closeSendLeadModal"
    />
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import BNavbar from "@/components/base/BNavbar.vue";
import BNavItemDropdown from "@/components/base/BNavItemDropdown.vue";
import BDropdownItem from "@/components/base/BDropdownItem.vue";
import BDropdownDivider from "@/components/base/BDropdownDivider.vue";
import BBadge from "@/components/base/BBadge.vue";
import CompanyDisplay from "@/components/companyDisplay/CompanyDisplay.vue";
import Contact from "./Contact.vue";
import { navTo } from "@/services/helpers";
import BulkDiscountModal from "@/components/storefront/BulkDiscountModal.vue";
import { listingRouteNames } from "@/routes/marketplaceRoutes";
import CompanyChatWindowWithTrigger from "@/components/chat/CompanyChatWindowWithTrigger.vue";
import StoreAuthModal from "@/components/storefront/auth/StoreAuthModal.vue";
import CompanySelector from "@/components/storefront/auth/CompanySelector.vue";
import CreateOperationModal from "@/components/storefront/auth/CreateOperationModal.vue";
import WaitApprovalModal from "@/components/storefront/auth/WaitApprovalModal.vue";
import SendLeadModal from "@/components/storefront/auth/SendLeadModal.vue";
import WhileWaitingModal from "@/components/storefront/auth/WhileWaitingModal.vue";
import VendorDisabledAlert from "@/components/storefront/VendorDisabledAlert.vue";
import * as api from "@/services/api";
import { brandLockParams, brandLockQuery, lockedBrandFromQuery } from "@/services/storefront/brandLock";
import { bulkDiscountsForBuyer } from "@/services/bulkDiscounts";

export default {
  components: {
    CompanyDisplay,
    Contact,
    BDropdownItem,
    BNavbar,
    BNavItemDropdown,
    BDropdownDivider,
    BBadge,
    BulkDiscountModal,
    CompanyChatWindowWithTrigger,
    StoreAuthModal,
    CompanySelector,
    CreateOperationModal,
    WaitApprovalModal,
    SendLeadModal,
    WhileWaitingModal,
    VendorDisabledAlert,
  },
  props: {},
  data() {
    return {
      industry: APP_INDUSTRY,
      appUrl: APP_URL,
      isShowingBulkDiscountModal: false,
      tab: "Menu",
      cdpModalState: false,
      showAuthModal: false,
      showCompanySelectorModal: false,
      cartVisible: false,
      company: {},
      theme: null,
      bulkDiscounts: [],
      loading: true,
      loadingCompany: true,
      loadingError: false,
      brandUnavailable: false,
      loadingUser: false,
      loggedIn: false,
      companies: [],
      buyers: [],
      cartOptions: [],
      cartError: null,
      activeCartDynamicIndex: null,
      showCreateOperationsModal: false,
      showWaitApprovalModal: false,
      showWhileWaitingModal: false,
      whileWaitingReason: "",
      showSendLeadModal: false,
      userCanShop: false,
      vendorDisabled: false,
      eligibilityError: null,
      showedAuth: false,
      resendingVerification: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
    }),
    vendorSlug() {
      return this.$route.params.vendorSlug;
    },
    view() {
      return this.$route.meta.view || "profile";
    },
    // Child routes keep the lock on the URL, and a widget's URL never changes, so the route is
    // the single source for both views.
    lockedBrand() {
      return lockedBrandFromQuery(this.$route.query);
    },
    // Everything the routed listing or product page needs from the storefront chrome. Bound
    // explicitly rather than provided, so the contract stays visible at the call site.
    listingProps() {
      return {
        // Only what this buyer can receive: none when they have bulk discounts disabled, and none
        // their pricing tiers overrule.
        bulkDiscounts: bulkDiscountsForBuyer(this.bulkDiscounts, this.activeBuyer),
        storefrontMode: true,
        storefrontCompany: this.company,
        storefrontTheme: this.theme,
        storefrontBuyer: this.activeBuyer,
        storefrontLockedBrand: this.lockedBrand,
        storefrontHidePrices: this.shouldHidePrices,
        storefrontHideQuantity: this.shouldHideQuantity,
        storefrontShoppingBlockReason: this.shoppingBlockReason,
        storefrontError: this.eligibilityError,
        storefrontCartError: this.cartError,
        // A widget renders in an iframe, where routing to a product page would hijack the host
        // page's history — it keeps the modal instead.
        urlContext: this.view === "widget" ? null : this.listingUrlContext,
      };
    },
    listingUrlContext() {
      return {
        basePath: `/storefront/${this.vendorSlug}`,
        routeNames: listingRouteNames("storefront"),
        routeParams: { vendorSlug: this.vendorSlug },
        subcategoryKey: "productTypeIds",
      };
    },
    massagedOptions() {
      return this.cartOptions
        .filter((op) => op.fillable == true)
        .map((val) => {
          const data = val;
          if (val.cart_items_count) {
            data.customOptionName = `${val.name} | ${val.cart_items_count}`;
          } else {
            data.customOptionName = val.name;
          }
          return data;
        });
    },
    activeBuyer() {
      if (!this.buyers.length || !this.activeCartDynamicIndex) return null;
      const matches = this.buyers.filter((b) => b.buyer_operation_id === this.activeCartDynamicIndex);
      // A buyer company can be bound to multiple CRM buyer records for the same operation.
      // Prefer an active one so a disabled duplicate doesn't incorrectly block ordering.
      return matches.find((b) => !b.disabled && !b.hold) ?? matches[0] ?? null;
    },
    activeBuyerCanOrder() {
      if (!this.activeBuyer) return true;
      return !this.activeBuyer.disabled && !this.activeBuyer.hold;
    },
    showProfileChat() {
      if (this.loggedIn && this.user && this.user.current_company_id && this.company && this.user.current_company_id != this.company.id) {
        return true;
      }
      return false;
    },
    // A shopper who isn't eligible sees neither prices nor quantities. On top of that, a seller
    // can hide either one independently through their theme, which is what makes "Please Inquire
    // For Price" reachable for a shopper who is otherwise allowed to order.
    canSeeStorefrontValues() {
      return Boolean(this.loggedIn && this.user && this.user.current_company && this.userCanShop);
    },
    shouldHidePrices() {
      return !this.canSeeStorefrontValues || this.themeHides("page_product_layout_product_price_visible");
    },
    shouldHideQuantity() {
      return !this.canSeeStorefrontValues || this.themeHides("page_product_layout_product_quantity_visible");
    },
    isOwnStorefront() {
      return this.loggedIn && this.user && this.company && this.user.current_company_id === this.company.id;
    },
    shoppingBlockReason() {
      if (this.isOwnStorefront) return "own_storefront";
      if (!this.loggedIn || !this.user || !this.user.current_company) return "login";
      if (!this.userCanShop) {
        if (this.whileWaitingReason === "account_needs_approval") return "account_not_approved";
        if (this.whileWaitingReason === "contact_not_in_crm") return "account_not_approved_four_day_rule";
        if (this.eligibilityError === "state restriction") return "state_restriction";
        if (this.eligibilityError === "block buyer order") return "buyer_blocked";
        return "company_inactive";
      }
      if (this.vendorDisabled) return "vendor_disabled";
      if (!this.activeBuyerCanOrder) return "buyer_operation_disabled";
      return null;
    },

    activeCart() {
      if (this.activeCartDynamicIndex) {
        return this.cartOptions.find((cart) => cart.dynamicIndex == this.activeCartDynamicIndex);
      }
      return {};
    },
  },
  mounted() {
    // A bare /storefront or /menu-widget names no vendor, so there is nothing to fetch — fall
    // straight through to the "menu unavailable" panel instead of requesting an unknown slug.
    if (!this.vendorSlug) {
      this.loading = false;
      this.loadingCompany = false;
      this.loadingError = true;

      return;
    }

    this.fetchStorefrontTheme();
    this.fetchPublicStorefrontCompany();
    this.fetchPublicStorefrontBulkDiscounts();
  },
  methods: {
    navTo,
    ...mapActions({
      fetchUserNoDispatching: "auth/fetchUserNoDispatching",
      setActiveCompany: "auth/setActiveCompanyNoDispatching",
      logout: "auth/logout",
    }),
    // These theme flags default to true and are nullable, so only an explicit false hides
    // anything. A storefront with no theme, or an older one missing the key, stays visible.
    themeHides(visibilityKey) {
      return this.theme ? this.theme[visibilityKey] === false : false;
    },
    async resendVerification() {
      this.resendingVerification = true;
      try {
        await api.post("/auth/email/resend", { redirect: window.location.pathname });
      } finally {
        this.resendingVerification = false;
      }
    },
    toggleCheckout() {
      const wasVisible = this.cartVisible;
      this.cartVisible = !this.cartVisible;
      if (wasVisible) {
        this.fetchCartOptions();
      }
    },
    canView() {
      try {
        if (window.self == window.top) {
          return true;
        }

        if (window.self !== window.top && this.view === "widget") {
          return true;
        }

        return false;
      } catch (e) {
        return false;
      }
    },
    redirectToStorefront() {
      const query = new URLSearchParams({ auth: "true", ...brandLockQuery(this.lockedBrand) });
      window.open(`/storefront/${this.vendorSlug}?${query}`, "_blank").focus();
    },
    showAuthFromQuery() {
      if (this.$route.query.auth === "true" && this.view == "profile" && !this.showedAuth) {
        this.showAuthModal = true;
        this.showedAuth = true;
      }
    },
    loginRoute() {
      window.open("/login", "_blank", "noopener,noreferrer");
    },
    registerRoute() {
      const redirect = encodeURIComponent(window.location.pathname);
      window.open(`/register?redirect=${redirect}`, "_blank", "noopener,noreferrer");
    },
    hasSwitchableCompanies() {
      if (this.companies.length > 0) {
        return true;
      }

      return false;
    },
    closeWaitApprovalModal() {
      this.showWaitApprovalModal = false;
    },

    closeWhileWaitingModal() {
      this.showWhileWaitingModal = false;
    },

    closeSendLeadModal() {
      this.showSendLeadModal = false;
    },
    onSelectCompany(company) {
      this.showCompanySelectorModal = false;
      this.setCurrentCompany(company);
    },
    onLogout() {
      this.logout();
      this.loggedIn = false;
    },
    onLoggedIn(loggedIn) {
      this.showAuthModal = false;
      this.isLoggedIn(false);
    },
    onRequestAccess() {
      this.sendCompanyLead();
      setTimeout(() => {
        this.showSendLeadModal = false;
      }, 1000);
    },
    setCurrentCompany(company) {
      this.setActiveCompany({
        id: this.user.id,
        current_company_id: company.id,
      }).then(() => {
        this.canShop();
      });
    },
    handleUpdateQuantityAction(data) {
      api.post("marketplace/cart/update-quantity", data).then(
        (response) => {
          if (response.data && response.data.cart) {
            this.handleLoadedCart(response.data.cart);
            this.fetchCartAction(this.activeCart, false);
          }
        },
        () => {},
      );
    },
    removeFromCartAction(data) {
      api.destroy(`marketplace/cart/remove/${data}`).then(
        (response) => {
          if (response.data && response.data.cart) {
            this.handleLoadedCart(response.data.cart);
            this.fetchCartAction(this.activeCart, false);
          }
        },
        () => {},
      );
    },
    fetchCartOptions() {
      api.get("/operations").then(
        (response) => {
          if (response.data) {
            const operations = response.data.filter(
              (op) =>
                op.line_one &&
                op.city &&
                op.zip &&
                op.state_id &&
                op.country_id &&
                !op.disabled &&
                !op.deleted_at &&
                (this.industry !== "cannabis" || op.state_license),
            );

            this.cartOptions = operations.map((op) => ({
              name: op.name,
              dynamicIndex: op.id,
              buyer_operation_id: op.id,
              cart_id: null,
              cart_items: [],
              cart_items_count: 0,
              fillable: true,
              suggested_cart: false,
            }));

            // Overlay cart counts from marketplace (filtered to this seller)
            api.get(`marketplace/carts-with-item-count?seller_company_id=${this.company.id}`).then((countResponse) => {
              if (countResponse.data) {
                const carts = countResponse.data;
                this.cartOptions = this.cartOptions.map((opt) => {
                  const match = carts.find((c) => c.buyer_operation_id === opt.buyer_operation_id);
                  if (match) {
                    return { ...opt, cart_id: match.id, cart_items_count: match.cart_items_count };
                  }
                  return opt;
                });
              }

              if (!this.activeCartDynamicIndex && this.cartOptions.length) {
                this.changeActiveCart(this.cartOptions[0]);
              }
            });
          }
        },
        () => {},
      );
    },
    fetchCartAction(cart) {
      this.changeActiveCart(cart);
      if (cart && cart.buyer_operation_id && (cart.cart_id || cart.cart_items_count > 0)) {
        const operationId = cart.buyer_operation_id;
        api.get(`marketplace/cart/${operationId}`).then(
          (response) => {
            if (response.data && response.data.carts) {
              const matchedCart = response.data.carts.find((c) => c && c.seller_company_id === this.company.id);
              if (matchedCart && matchedCart.cart_items) {
                this.handleLoadedCart(matchedCart);
              }
            }
          },
          () => {},
        );
      }
    },
    changeActiveCart(cart, fetchCart = false) {
      this.activeCartDynamicIndex = cart.dynamicIndex;
      if (fetchCart) {
        this.fetchCartAction(cart);
      }
    },
    handleLoadedCart(data) {
      const newOptions = this.cartOptions.map((cart) => {
        const newCart = { ...cart };
        if (newCart.cart_id == data.id || (!newCart.cart_id && newCart.dynamicIndex === this.activeCartDynamicIndex)) {
          newCart.cart_items = data.cart_items;
          newCart.cart_items_count = data.cart_items ? data.cart_items.length : 0;
          newCart.cart_id = data.id;
        }
        return newCart;
      });
      this.cartOptions = newOptions;
    },
    addToCartAction(data) {
      const payload = {
        buyer_operation_id: this.activeCart.buyer_operation_id,
        items: data.cartItems,
        ...brandLockParams(this.lockedBrand),
      };
      this.cartError = null;
      api.patch("marketplace/carts/update", payload).then(
        (response) => {
          // A rejected line still returns 200 with the reason in error_warning.
          this.cartError = response.data?.error_warning ?? null;

          if (response.data && response.data.cart) {
            const baseCart = response.data.cart;
            if (baseCart && baseCart.carts) {
              const sellerCart = baseCart.carts.find((c) => c.seller_company_id === this.company.id);
              if (sellerCart) {
                const items = sellerCart.cart_items || [];
                const newOptions = this.cartOptions.map((cart) => {
                  const newCart = { ...cart };
                  if (newCart.buyer_operation_id == this.activeCart.buyer_operation_id) {
                    newCart.cart_items = items;
                    newCart.cart_items_count = items.length;
                    newCart.cart_id = sellerCart.id;
                  }
                  return newCart;
                });
                this.cartOptions = newOptions;
              }
            }
          }
        },
        (error) => {
          this.cartError = error?.response?.data?.message ?? "This product could not be added to your cart.";
        },
      );
    },
    fetchPublicStorefrontBulkDiscounts() {
      const slug = this.vendorSlug;
      api.get(`storefront/public-bulk-discounts/${slug}`, { params: brandLockParams(this.lockedBrand) }).then(
        (response) => {
          if (response.data && response.data) {
            this.bulkDiscounts = response.data;
          } else {
            // problem
          }
        },
        (error) => {
          // problem
        },
      );
    },
    fetchPublicStorefrontCompany() {
      const slug = this.vendorSlug;
      api.get(`company/by-slug/${slug}`).then(
        (response) => {
          if (response.data && response.data.data) {
            this.company = response.data.data;
            this.loadingCompany = false;
            this.isLoggedIn();
          } else {
            this.loadingError = true;
            this.loadingCompany = false;
          }
        },
        (error) => {
          this.loadingError = true;
          this.loadingCompany = false;
        },
      );
    },
    fetchStorefrontTheme() {
      const slug = this.vendorSlug;
      const params = new URL(window.location).searchParams;
      const theme = params.get("theme");
      const uri = `storefront/public-theme/by-slug/${slug}/${this.view}`;
      api.get(uri, { params: { ...(theme ? { theme } : {}), ...brandLockParams(this.lockedBrand) } }).then(
        (response) => {
          this.loading = false;
          if (response.data) {
            this.theme = response.data.theme;
          }
        },
        (error) => {
          this.loading = false;
          // The theme endpoint is the first to resolve the lock, so it decides whether the brand exists.
          this.brandUnavailable = Boolean(this.lockedBrand) && error?.response?.status === 404;
        },
      );
    },
    fetchUserCompanies() {
      api.get("/companies").then(
        (response) => {
          if (response.data && response.data.length > 0) {
            this.companies = response.data;
          }
        },
        () => {},
      );
    },

    canShop() {
      return api.post(`storefront/shoppable/${this.company.id}`).then((response) => {
        if (response.data && response.data.status) {
          this.vendorDisabled = Boolean(response.data.vendor_disabled);
          if (response.data.status.toLowerCase() == "success") {
            this.userCanShop = true;
            this.fetchBuyers();
            this.fetchCartOptions();
          } else {
            this.userCanShop = false;

            const message = response.data.message.toLowerCase();
            if (message == "no operation") {
              this.showCreateOperationsModal = true;
            } else if (message == "company not approved" || message == "account not approved") {
              this.whileWaitingReason = "account_needs_approval";
              this.showWhileWaitingModal = true;
            } else if (message == "contact not in seller crm") {
              this.whileWaitingReason = "contact_not_in_crm";
              this.showWhileWaitingModal = true;
            } else {
              this.eligibilityError = message;
            }
          }
        }
      });
    },
    fetchBuyers() {
      api.get(`storefront/buyers/${this.company.id}`).then(
        (response) => {
          if (response.data) {
            this.buyers = response.data;
          }
        },
        () => {},
      );
    },
    onOperationCreated() {
      this.showCreateOperationsModal = false;
      this.canShop();
    },
    sendCompanyLead() {
      api
        .post("storefront-messages", {
          company_id: this.company.id,
          company_name: this.user.current_company.name,
          contact_name: this.user.name,
          email: this.user.email,
          phone: this.user.phone,
          message: "I am interested in becoming a buyer for your company.",
          source: "Buyer StoreFront",
          requesting_company_id: this.user.current_company_id,
        })
        .then(
          (response) => {
            this.canShop();
          },
          (error) => {
            // problem
          },
        );
    },
    isLoggedIn(showLoading = true) {
      this.loadingUser = showLoading;
      this.loggedIn = false;
      this.fetchUserNoDispatching().then(
        () => {
          this.loadingUser = false;
          if (this.user.current_company) {
            this.loggedIn = true;
            if (this.user.email_verified_at) {
              // Redirect verified users to the in-app vendor storefront, keeping whatever
              // filters, sort or product they were looking at.
              if (this.view !== "widget") {
                const rest = this.$route.fullPath.replace(/^\/storefront\/[^/?#]+/, "");
                window.location.href = `/vendors/${this.vendorSlug}${rest}`;
                return;
              }
              this.fetchUserCompanies();
              this.canShop();
            }
          } else {
            this.showAuthFromQuery();
          }
        },
        (error) => {
          this.loadingUser = false;
          this.loggedIn = false;
        },
      );
    },
  },
};
</script>

<style scoped>
.storefront-cart-select {
  min-width: 100px;
  max-width: 180px;
}

@media (min-width: 576px) {
  .storefront-cart-select {
    max-width: none;
  }
}

@media (max-width: 575.98px) {
  .storefront-chat-trigger :deep(button) {
    font-size: 0;
  }

  .storefront-chat-trigger :deep(button) > span {
    font-size: initial;
  }
}
</style>
